import { getSafeSheetName } from "./sheetUtils";

// ═══ i18n dictionary for BOQ sheet labels ═══
const BOQ_TRANSLATIONS = {
  geo: {
    project_name_label: "პროექტის დასახელება:",
    date: "თარიღი:",
    customer: "დამკვეთი",
    doc_title: "დოკუმენტის დასახელება",
    doc_subtitle: "საორიენტაციო ღირებულება",
    doc_number: "დოკ. ნომერი",
    location: "მდებარეობა",
    tech_spec: "ტექნიკური მახასიათებელი",
    area_unit: "მ²",
    col_n: "N",
    col_desc: "სამუშაოების დასახელება",
    col_material: "მასალა",
    col_labour: "ხელფასი",
    col_equipment: "მექანიზმი",
    col_total: "ჯამური ღირებულება",
    col_percent: "% / 100 %",
    sum: "ჯამი",
    overhead_5: (pct) => `გაუთვალისწინებელი ხარჯები ${pct}%`,
    vat_18: (pct) => `დ.ღ.გ. ${pct}%`,
    signed_by: "შეასრულა:",
  },
  eng: {
    project_name_label: "Project name:",
    date: "Date:",
    customer: "Customer",
    doc_title: "Document title",
    doc_subtitle: "Indicative cost",
    doc_number: "Doc. number",
    location: "Location",
    tech_spec: "Technical spec",
    area_unit: "m²",
    col_n: "N",
    col_desc: "Description of works",
    col_material: "Material",
    col_labour: "Labour",
    col_equipment: "Equipment",
    col_total: "Total cost",
    col_percent: "% / 100 %",
    sum: "Total",
    overhead_5: (pct) => `Contingency ${pct}%`,
    vat_18: (pct) => `VAT ${pct}%`,
    signed_by: "Prepared by:",
  },
  rus: {
    project_name_label: "Наименование проекта:",
    date: "Дата:",
    customer: "Заказчик",
    doc_title: "Наименование документа",
    doc_subtitle: "Ориентировочная стоимость",
    doc_number: "№ док.",
    location: "Расположение",
    tech_spec: "Технические характеристики",
    area_unit: "м²",
    col_n: "№",
    col_desc: "Наименование работ",
    col_material: "Материал",
    col_labour: "Зарплата",
    col_equipment: "Механизм",
    col_total: "Общая стоимость",
    col_percent: "% / 100 %",
    sum: "Итого",
    overhead_5: (pct) => `Непредвиденные расходы ${pct}%`,
    vat_18: (pct) => `НДС ${pct}%`,
    signed_by: "Выполнил:",
  },
};

function pickLocale(lang) {
  const key = String(lang || "geo").toLowerCase();
  if (key === "eng" || key === "en" || key === "english") return "eng";
  if (key === "rus" || key === "ru" || key === "russian") return "rus";
  return "geo";
}

// All possible "total / sum" / summary labels across languages — used when reading back.
// overhead_5 and vat_18 are now functions so we call them with common pct values to seed the set.
const SUMMARY_STOP_WORDS = new Set([
  ...Object.values(BOQ_TRANSLATIONS).map((t) => t.sum),
  ...Object.values(BOQ_TRANSLATIONS).flatMap((t) => [t.overhead_5(5), t.overhead_5(10), t.overhead_5(0)]),
  ...Object.values(BOQ_TRANSLATIONS).flatMap((t) => [t.vat_18(18), t.vat_18(20), t.vat_18(0)]),
  ...Object.values(BOQ_TRANSLATIONS).map((t) => t.signed_by),
]);

// "1.0" -> "1" so legacy auto-numbers and plain chapter numbers compare equal.
export function normalizeBoqNumber(value) {
  return String(value ?? "").trim().replace(/\.0+$/, "");
}

/**
 * Parent/child structure of the root BOQ rows, derived from their numbers:
 * "2" is the parent of "2.1", "2.2", ... (and "2.1" of "2.1.1"). Descendants
 * must sit directly under their parent. Returns, per row, its depth (`level`)
 * and the index of its last descendant (`lastDescendant` === row index for a
 * leaf).
 */
export function computeBoqHierarchy(numbers) {
  const keys = numbers.map(normalizeBoqNumber);
  return keys.map((key, i) => {
    let last = i;
    if (key) {
      while (last + 1 < keys.length && keys[last + 1].startsWith(key + ".")) last += 1;
    }
    return { key, level: key ? key.split(".").length - 1 : 0, lastDescendant: last };
  });
}

/**
 * TOTAL (root) BOQ sheet — 3-language template (GEO / ENG / RUS).
 *
 * Columns B–K:
 *   B = N
 *   C–F = description (merged)
 *   G = Material
 *   H = Labour
 *   I = Equipment
 *   J = Total cost
 *   K = % / 100 %
 */
export async function writeBoqTable(projectData, boqData, childTotals = {}, sheetTitle = "BOQ0", childSheetRefs = {}) {
  const L = BOQ_TRANSLATIONS[pickLocale(projectData?.language)];
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheetName = getSafeSheetName(sheetTitle || "BOQ0");
    let sheet = workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) {
      sheet = workbook.worksheets.add(sheetName);
    } else {
      sheet.getUsedRange().clear(Excel.ClearApplyTo.all);
    }
    sheet.activate();
    await context.sync();

    const CUR_FMT = '#,##0.00 "₾"';

    // ── Page setup (A4 landscape, fit to 1 page wide) ──
    const pl = sheet.pageLayout;
    pl.orientation = "Landscape";
    pl.paperSize = "A4";
    pl.fitToPage = true;
    pl.fitToWidth = 1;
    pl.fitToHeight = 0;
    pl.topMargin = 0.5;
    pl.bottomMargin = 0.5;
    pl.leftMargin = 0.5;
    pl.rightMargin = 0.5;
    await context.sync();

    // ── Column widths (pixels) — tuned for A4 landscape ──
    sheet.getRange("A:A").format.columnWidth = 18;
    sheet.getRange("B:B").format.columnWidth = 42;
    sheet.getRange("C:C").format.columnWidth = 150;
    sheet.getRange("D:D").format.columnWidth = 95;
    sheet.getRange("E:E").format.columnWidth = 85;
    sheet.getRange("F:F").format.columnWidth = 80;
    sheet.getRange("G:G").format.columnWidth = 88;
    sheet.getRange("H:H").format.columnWidth = 88;
    sheet.getRange("I:I").format.columnWidth = 88;
    sheet.getRange("J:J").format.columnWidth = 98;
    sheet.getRange("K:K").format.columnWidth = 68;
    await context.sync();

    // ── Project info ──
    const projectName = projectData?.name || projectData?.description || "";
    const projectDate = projectData?.date || "";
    const customerName = projectData?.customer || "";
    const projectArea = projectData?.quantity || projectData?.area || "";
    const docNumber = projectData?.ref || "";
    const locationText = projectData?.location || "";

    // ── Row heights ──
    sheet.getRange("2:2").format.rowHeight = 28;
    sheet.getRange("3:3").format.rowHeight = 5;
    sheet.getRange("4:4").format.rowHeight = 20;
    sheet.getRange("5:5").format.rowHeight = 20;
    sheet.getRange("6:6").format.rowHeight = 18;
    sheet.getRange("7:7").format.rowHeight = 6;
    sheet.getRange("8:8").format.rowHeight = 6;
    sheet.getRange("9:9").format.rowHeight = 20;
    sheet.getRange("10:10").format.rowHeight = 20;
    sheet.getRange("11:11").format.rowHeight = 20;
    sheet.getRange("12:12").format.rowHeight = 20;
    sheet.getRange("13:13").format.rowHeight = 6;
    sheet.getRange("14:14").format.rowHeight = 6;
    await context.sync();

    // Row 2: Project name banner
    sheet.getRange("B2").values = [[L.project_name_label + "  " + projectName]];
    await context.sync();
    sheet.getRange("B2:K2").merge();
    const bannerRange = sheet.getRange("B2:K2");
    bannerRange.format.fill.color = "#00B3B3";
    bannerRange.format.font.color = "white";
    bannerRange.format.font.bold = true;
    bannerRange.format.font.size = 12;
    bannerRange.format.verticalAlignment = "Center";
    bannerRange.format.horizontalAlignment = "Left";
    await context.sync();

    // Row 4: [Date label | date value] [Customer label | customer name]
    sheet.getRange("B4").values = [[L.date]];
    sheet.getRange("D4").values = [[projectDate]];
    sheet.getRange("F4").values = [[L.customer]];
    sheet.getRange("H4").values = [[customerName]];
    await context.sync();
    sheet.getRange("B4:C4").merge();
    sheet.getRange("D4:E4").merge();
    sheet.getRange("F4:G4").merge();
    sheet.getRange("H4:K4").merge();
    await context.sync();

    // Row 5: [Doc No label | value] [Location label | value] [area+spec]
    sheet.getRange("B5").values = [[L.doc_number]];
    sheet.getRange("D5").values = [[docNumber]];
    sheet.getRange("F5").values = [[L.location]];
    sheet.getRange("H5").values = [[locationText]];
    sheet.getRange("J5").values = [[projectArea ? L.tech_spec + ": " + String(projectArea) + " " + L.area_unit : ""]];
    await context.sync();
    sheet.getRange("B5:C5").merge();
    sheet.getRange("D5:E5").merge();
    sheet.getRange("F5:G5").merge();
    sheet.getRange("H5:I5").merge();
    sheet.getRange("J5:K5").merge();
    await context.sync();

    // Row 6: Document subtitle — full width, subtle
    sheet.getRange("B6").values = [[L.doc_title + " — " + L.doc_subtitle]];
    await context.sync();
    sheet.getRange("B6:K6").merge();
    const subtitleRange = sheet.getRange("B6:K6");
    subtitleRange.format.horizontalAlignment = "Center";
    subtitleRange.format.font.italic = true;
    subtitleRange.format.font.size = 10;
    subtitleRange.format.font.color = "#1B2A33";
    await context.sync();

    // Style info block
    const infoRange = sheet.getRange("B4:K5");
    infoRange.format.font.bold = false;
    infoRange.format.font.size = 10;
    infoRange.format.verticalAlignment = "Center";
    // Label cells: bold + right-aligned to sit flush against value
    ["B4", "F4", "B5", "F5"].forEach((addr) => {
      sheet.getRange(addr).format.font.bold = true;
      sheet.getRange(addr).format.horizontalAlignment = "Right";
    });
    // Value cells: left-aligned
    ["D4", "H4", "D5", "H5", "J5"].forEach((addr) => {
      sheet.getRange(addr).format.horizontalAlignment = "Left";
    });
    await context.sync();

    // ── Column headers (rows 9–11) ──
    sheet.getRange("B9").values = [[L.col_n]];
    sheet.getRange("C9").values = [[L.col_desc]];
    sheet.getRange("G9").values = [[L.col_material]];
    sheet.getRange("H9").values = [[L.col_labour]];
    sheet.getRange("I9").values = [[L.col_equipment]];
    sheet.getRange("J9").values = [[L.col_total]];
    sheet.getRange("K9").values = [[L.col_percent]];
    await context.sync();

    // Merge header cells
    sheet.getRange("B9:B11").merge();
    sheet.getRange("C9:F11").merge();
    sheet.getRange("G9:G11").merge();
    sheet.getRange("H9:H11").merge();
    sheet.getRange("I9:I11").merge();
    sheet.getRange("J9:J11").merge();
    sheet.getRange("K9:K11").merge();
    await context.sync();

    const headerRange = sheet.getRange("B9:K11");
    headerRange.format.fill.color = "#1B2A33";
    headerRange.format.font.color = "white";
    headerRange.format.font.bold = true;
    headerRange.format.font.size = 10;
    headerRange.format.horizontalAlignment = "Center";
    headerRange.format.verticalAlignment = "Center";
    headerRange.format.wrapText = true;
    await context.sync();

    // Row 12: Column numbers (teal)
    sheet.getRange("B12").values = [["1"]];
    sheet.getRange("C12").values = [["2"]];
    sheet.getRange("G12").values = [["3"]];
    sheet.getRange("H12").values = [["4"]];
    sheet.getRange("I12").values = [["5"]];
    sheet.getRange("J12").values = [["6"]];
    sheet.getRange("K12").values = [["7"]];
    await context.sync();
    sheet.getRange("C12:F12").merge();
    await context.sync();
    const numRow = sheet.getRange("B12:K12");
    numRow.format.fill.color = "#00B3B3";
    numRow.format.font.color = "white";
    numRow.format.font.bold = true;
    numRow.format.horizontalAlignment = "Center";
    await context.sync();

    // ── Data rows (row 15+) ──
    const DATA_START = 15;
    const MIN_ROWS = 10;

    // Row numbers: keep the number typed/saved for the row ("2", "2.1", ...),
    // otherwise auto-number as the next top-level chapter.
    const rowNumbers = [];
    let topCounter = 0;
    (boqData || []).forEach((item) => {
      if (!(item.description || "").trim()) {
        rowNumbers.push("");
        return;
      }
      const given = normalizeBoqNumber(item.number);
      if (given) {
        if (/^\d+$/.test(given)) topCounter = Number(given);
        rowNumbers.push(given);
      } else {
        topCounter += 1;
        rowNumbers.push(String(topCounter));
      }
    });

    const items = [];
    if (boqData && boqData.length > 0) {
      boqData.forEach((item, index) => {
        const ct = childTotals[index];
        const matCost = item.materialCost || item.mat_total || 0;
        const labCost = item.labourCost || item.salary_total || 0;
        const eqCost = item.equipmentCost || item.transport_total || 0;

        const mat = ct?.material !== undefined ? ct.material : matCost;
        const lab = ct?.labour !== undefined ? ct.labour : labCost;
        const eq = ct?.equipment !== undefined ? ct.equipment : eqCost;
        const tot = ct?.total !== undefined ? ct.total
          : (typeof ct === "number" ? ct : (mat + lab + eq) || item.totalPrice || 0);

        const hasDesc = (item.description || "").trim().length > 0;
        items.push([
          rowNumbers[index] || "",
          item.description || "",
          "", "", "",
          hasDesc && mat ? mat : "",
          hasDesc && lab ? lab : "",
          hasDesc && eq ? eq : "",
          hasDesc && tot ? tot : "",
          item.percentage || "",
        ]);
      });
    }
    while (items.length < MIN_ROWS) {
      items.push(["", "", "", "", "", "", "", "", "", ""]);
    }

    const dataEnd = DATA_START + items.length - 1;

    // Number column is text so "2.10" is not turned into 2.1 (or a date).
    sheet.getRangeByIndexes(DATA_START - 1, 1, items.length, 1).numberFormat =
      items.map(() => ["@"]);

    // Write all data at once
    sheet.getRangeByIndexes(DATA_START - 1, 1, items.length, 10).values = items;
    await context.sync();

    // Parent/child structure from the numbers: "2" is the parent of 2.1, 2.2, ...
    const hierarchy = computeBoqHierarchy(items.map((it) => it[0]));

    // Parents with children sum their children (SUBTOTAL skips nested subtotals,
    // so the grand total below never double-counts). Leaf rows keep their link
    // to the child detail sheet.
    // Child sheet column mapping: G(mat)→G, I(lab)→H, K(eq)→I, L(total)→J
    for (let i = 0; i < items.length; i++) {
      const r = DATA_START + i;
      const { lastDescendant } = hierarchy[i];
      if (lastDescendant > i) {
        ["G", "H", "I", "J"].forEach((col) => {
          sheet.getRange(`${col}${r}`).formulas = [[`=SUBTOTAL(9,${col}${r + 1}:${col}${DATA_START + lastDescendant})`]];
        });
        continue;
      }

      const ref = childSheetRefs[i];
      if (!ref || !ref.sheetName || !ref.summaryRow) continue;

      const sn = "'" + ref.sheetName.replace(/'/g, "''") + "'";
      const sr = ref.summaryRow;
      sheet.getRange(`G${r}`).formulas = [[`=${sn}!G${sr}`]];   // Material
      sheet.getRange(`H${r}`).formulas = [[`=${sn}!I${sr}`]];   // Labour
      sheet.getRange(`I${r}`).formulas = [[`=${sn}!K${sr}`]];   // Equipment
      sheet.getRange(`J${r}`).formulas = [[`=${sn}!L${sr}`]];   // Total
    }
    await context.sync();

    // Merge description columns and style each row
    for (let i = 0; i < items.length; i++) {
      const r = DATA_START + i;
      sheet.getRange(`C${r}:F${r}`).merge();
    }
    await context.sync();

    for (let i = 0; i < items.length; i++) {
      const r = DATA_START + i;
      const rowRange = sheet.getRange(`B${r}:K${r}`);
      const { level } = hierarchy[i];
      // Sub-rows (2.1, 2.2, ...) get a lighter fill, normal weight and an indent.
      rowRange.format.fill.color = level > 0 ? "#FFFFFF" : i % 2 === 0 ? "#DEEAF6" : "#EDF2F7";
      rowRange.format.verticalAlignment = "Center";

      if (items[i][1]) {
        sheet.getRange(`G${r}:J${r}`).numberFormat = [[CUR_FMT, CUR_FMT, CUR_FMT, CUR_FMT]];
        sheet.getRange(`G${r}:J${r}`).format.horizontalAlignment = "Right";
        rowRange.format.font.bold = level === 0;
        if (level > 0) {
          sheet.getRange(`C${r}`).format.horizontalAlignment = "Left";
          sheet.getRange(`C${r}`).format.indentLevel = level;
        }
      }

      sheet.getRange(`B${r}`).format.horizontalAlignment = "Center";
    }
    await context.sync();

    // ── Summary block ──
    const cfg = projectData?.costConfig || {};
    const unforeseenPct = cfg.unforeseen?.enabled !== false ? (cfg.unforeseen?.pct ?? 5) : 0;
    const vatPct        = cfg.vat?.enabled        !== false ? (cfg.vat?.pct        ?? 18) : 0;
    const unforeseenRate = unforeseenPct / 100;
    const vatRate        = vatPct / 100;
    const extras = (cfg.extra || []).filter((e) => e.enabled !== false);

    const S = dataEnd + 2;

    // Base 5 rows: sum, unforeseen, sum, vat, sum
    const summaryLabels = [L.sum, L.overhead_5(unforeseenPct), L.sum, L.vat_18(vatPct), L.sum];
    // Append extra pairs: label, sum
    extras.forEach((e) => { summaryLabels.push(`${e.name} ${e.pct}%`, L.sum); });

    const totalSummaryRows = summaryLabels.length;
    for (let i = 0; i < totalSummaryRows; i++) {
      sheet.getRange(`C${S + i}`).values = [[summaryLabels[i]]];
    }
    await context.sync();

    for (let i = 0; i < totalSummaryRows; i++) {
      sheet.getRange(`C${S + i}:F${S + i}`).merge();
    }
    await context.sync();

    // Formulas for columns G, H, I, J
    ["G", "H", "I", "J"].forEach((col) => {
      sheet.getRange(`${col}${S}`).formulas = [[`=SUBTOTAL(9,${col}${DATA_START}:${col}${dataEnd})`]];
      sheet.getRange(`${col}${S + 1}`).formulas = [[`=${col}${S}*${unforeseenRate}`]];
      sheet.getRange(`${col}${S + 2}`).formulas = [[`=${col}${S}+${col}${S + 1}`]];
      sheet.getRange(`${col}${S + 3}`).formulas = [[`=${col}${S + 2}*${vatRate}`]];
      sheet.getRange(`${col}${S + 4}`).formulas = [[`=${col}${S + 2}+${col}${S + 3}`]];
      // Extra cost pairs
      extras.forEach((e, idx) => {
        const base = S + 4 + idx * 2;
        const rate = (e.pct || 0) / 100;
        sheet.getRange(`${col}${base + 1}`).formulas = [[`=${col}${base}*${rate}`]];
        sheet.getRange(`${col}${base + 2}`).formulas = [[`=${col}${base}+${col}${base + 1}`]];
      });
    });
    await context.sync();

    // Style summary rows (teal for sums, dark for percent rows)
    for (let i = 0; i < totalSummaryRows; i++) {
      const r = S + i;
      const isSumRow = i % 2 === 0;
      const row = sheet.getRange(`B${r}:K${r}`);
      row.format.fill.color = isSumRow ? "#00B3B3" : "#1B2A33";
      row.format.font.color = "white";
      row.format.font.bold = true;
      row.format.verticalAlignment = "Center";
      sheet.getRange(`C${r}`).format.horizontalAlignment = "Right";
      sheet.getRange(`G${r}:J${r}`).numberFormat = [[CUR_FMT, CUR_FMT, CUR_FMT, CUR_FMT]];
      sheet.getRange(`G${r}:J${r}`).format.horizontalAlignment = "Right";
    }
    await context.sync();

    // ── Footer ──
    const F = S + totalSummaryRows + 1;
    sheet.getRange(`C${F}`).values = [[L.signed_by]];
    sheet.getRange(`D${F}`).values = [[projectData?.author || ""]];
    sheet.getRange(`K${F}`).values = [["www.boq.ge"]];
    await context.sync();
    const footerRange = sheet.getRange(`B${F}:K${F}`);
    footerRange.format.fill.color = "#00B3B3";
    footerRange.format.font.color = "white";
    footerRange.format.font.bold = true;
    footerRange.format.verticalAlignment = "Center";

    await context.sync();
  });
}

// All "sum" / "total" words across languages for matching the summary row.
const SUM_WORDS = new Set(
  Object.values(BOQ_TRANSLATIONS).map((t) => t.sum)
);

/**
 * Finds the last summary (sum/total) row number in a child sheet.
 * Returns the 1-based row number, or 0 if not found.
 */
export async function findChildSheetSummaryRow(sheetTitle) {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheetName = getSafeSheetName(sheetTitle || "BOQ1");
    const sheet = workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) return 0;

    const used = sheet.getUsedRangeOrNullObject();
    used.load("values");
    await context.sync();

    if (used.isNullObject || !used.values || !used.values.length) return 0;

    const values = used.values;
    for (let i = values.length - 1; i >= 0; i--) {
      const label = String(values[i][1] || "").trim();
      if (SUM_WORDS.has(label)) {
        return i + 1;
      }
    }

    return 0;
  });
}

// "='1.0_ABC-დეტალური'!L29" -> { sheetName, summaryRow }
function parseChildRef(formula) {
  const m = /^=\s*'((?:[^']|'')+)'!L(\d+)$/.exec(String(formula || ""));
  return m ? { sheetName: m[1].replace(/''/g, "'"), summaryRow: Number(m[2]) } : null;
}

/** Row number of the active cell if the active sheet is `sheetTitle`, else null. */
export async function getSelectedBoqSheetRow(sheetTitle) {
  return Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load("rowIndex");
    range.worksheet.load("name");
    await context.sync();
    return range.worksheet.name === getSafeSheetName(sheetTitle) ? range.rowIndex + 1 : null;
  });
}

/**
 * Adds a row to the list read from the TOTAL sheet.
 *  - kind "parent": new top-level chapter at the end (next number: 3, 4, ...).
 *  - kind "child": new sub-row under the selected row (any level), after its
 *    last existing child (2.1, 2.2, ... or 2.1.1, ... — numbers of existing
 *    rows never change). To add a sibling, select the parent row instead.
 */
export function insertBoqRow(rows, { kind, name, selectedSheetRow, parentKey: explicitParentKey }) {
  // Give rows without a number the next top-level number first.
  let top = 0;
  const numbered = rows.map((r) => {
    const given = normalizeBoqNumber(r.number);
    if (given) {
      if (/^\d+$/.test(given)) top = Number(given);
      return { ...r, number: given };
    }
    top += 1;
    return { ...r, number: String(top) };
  });
  const blank = {
    description: name, materialCost: 0, labourCost: 0, equipmentCost: 0,
    totalPrice: 0, percentage: "", childRef: null,
  };

  if (kind === "parent") {
    const next = Math.max(0, ...numbered.filter((r) => /^\d+$/.test(r.number)).map((r) => Number(r.number))) + 1;
    return [...numbered, { ...blank, number: String(next) }];
  }

  const sel = explicitParentKey
    ? numbered.findIndex((r) => r.number === explicitParentKey)
    : numbered.findIndex((r) => r.sheetRow === selectedSheetRow);
  if (sel < 0) throw new Error("Click a cell in the chapter row you want to add a sub-item to.");
  const h = computeBoqHierarchy(numbered.map((r) => r.number));
  // The new row is a child of the selected row at any depth (2 -> 2.1 -> 2.1.1).
  const parentKey = h[sel].key;
  const parentIdx = sel;
  const last = h[parentIdx].lastDescendant;
  let maxChild = 0;
  for (let i = parentIdx + 1; i <= last; i += 1) {
    if (h[i].level === h[parentIdx].level + 1) {
      maxChild = Math.max(maxChild, Number(h[i].key.split(".").pop()) || 0);
    }
  }
  const out = [...numbered];
  out.splice(last + 1, 0, { ...blank, number: `${parentKey}.${maxChild + 1}` });
  return out;
}

/**
 * Reads BOQ data from the TOTAL sheet.
 */
export async function readBoqTable(sheetTitle = "BOQ0") {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheetName = getSafeSheetName(sheetTitle || "BOQ0");
    const sheet = workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) return [];

    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load("rowCount");
    await context.sync();

    if (usedRange.isNullObject) return [];

    const DATA_START = 15;
    const maxRows = Math.max(usedRange.rowCount - DATA_START + 1, 10);
    if (maxRows <= 0) return [];

    const dataRange = sheet.getRangeByIndexes(DATA_START - 1, 1, maxRows, 10); // B:K
    dataRange.load("values,formulas");
    await context.sync();

    const rows = dataRange.values || [];
    const formulas = dataRange.formulas || [];
    const parsedRows = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const desc = row[1] != null ? String(row[1]).trim() : "";
      if (SUMMARY_STOP_WORDS.has(desc)) break;

      if (!desc) continue;

      const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

      parsedRows.push({
        N: i + 1,
        number: normalizeBoqNumber(row[0]),
        sheetRow: DATA_START + i,
        // Link to the child detail sheet's summary row, parsed from the Total formula.
        childRef: parseChildRef(formulas[i]?.[8]),
        description: desc,
        materialCost: toNum(row[5]),
        labourCost: toNum(row[6]),
        equipmentCost: toNum(row[7]),
        totalPrice: toNum(row[8]),
        percentage: row[9] != null ? String(row[9]).trim() : "",
      });
    }

    return parsedRows;
  });
}

/**
 * Cost summary of the TOTAL sheet: material/labour/equipment/direct from the
 * first "ჯამი" row, `grand` from the last one (after unforeseen costs + VAT).
 * Returns null if the sheet or its summary rows are missing.
 */
export async function readBoqSummary(sheetTitle = "BOQ0") {
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItemOrNullObject(getSafeSheetName(sheetTitle || "BOQ0"));
    sheet.load("name");
    await context.sync();
    if (sheet.isNullObject) return null;

    const used = sheet.getUsedRangeOrNullObject();
    used.load("rowCount");
    await context.sync();
    if (used.isNullObject) return null;

    const DATA_START = 15;
    const rowCount = Math.max(used.rowCount + 2, 40);
    const range = sheet.getRangeByIndexes(DATA_START - 1, 1, rowCount, 10); // B:K
    range.load("values");
    await context.sync();

    const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
    const sums = range.values.filter((row) => SUM_WORDS.has(String(row[1] ?? "").trim()));
    if (!sums.length) return null;
    const first = sums[0];
    const last = sums[sums.length - 1];
    return {
      material: toNum(first[5]),
      labour: toNum(first[6]),
      equipment: toNum(first[7]),
      direct: toNum(first[8]),
      grand: toNum(last[8]),
    };
  });
}

/**
 * Reads child sheet totals { material, labour, equipment, total }.
 */
export async function readChildSheetTotal(sheetTitle) {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheetName = getSafeSheetName(sheetTitle || "BOQ1");
    const sheet = workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) {
      return { material: 0, labour: 0, equipment: 0, total: 0 };
    }

    const used = sheet.getUsedRangeOrNullObject();
    used.load("values");
    await context.sync();

    if (used.isNullObject || !used.values || !used.values.length) {
      return { material: 0, labour: 0, equipment: 0, total: 0 };
    }

    const values = used.values;
    const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

    // Find LAST "sum/total" row in any supported language
    for (let i = values.length - 1; i >= 0; i--) {
      const label = String(values[i][1] || "").trim();
      if (SUM_WORDS.has(label)) {
        return {
          material: toNum(values[i][6]),   // G
          labour: toNum(values[i][8]),     // I
          equipment: toNum(values[i][10]), // K
          total: toNum(values[i][11]),     // L
        };
      }
    }

    // Fallback: sum data rows. Skip any summary/overhead labels across all languages.
    let material = 0, labour = 0, equipment = 0;
    for (let i = 4; i < values.length; i++) {
      if (SUMMARY_STOP_WORDS.has(String(values[i][1] || "").trim())) continue;
      material += toNum(values[i][6]);
      labour += toNum(values[i][8]);
      equipment += toNum(values[i][10]);
    }
    return { material, labour, equipment, total: material + labour + equipment };
  });
}
